// Mobile Performance & UX Utilities

/**
 * Lazy load images using Intersection Observer
 * Usage: <img data-lazy-src="url" class="lazy-image" />
 */
export const initLazyLoading = () => {
  if (!('IntersectionObserver' in window)) {
    // Fallback for older browsers
    const images = document.querySelectorAll('[data-lazy-src]');
    images.forEach((img) => {
      img.src = img.dataset.lazySrc;
    });
    return;
  }

  const imageObserver = new IntersectionObserver((entries) => {
    entries.forEach((entry) => {
      if (entry.isIntersecting) {
        const img = entry.target;
        img.src = img.dataset.lazySrc;
        img.classList.add('loaded');
        imageObserver.unobserve(img);
      }
    });
  }, {
    rootMargin: '50px'
  });

  document.querySelectorAll('[data-lazy-src]').forEach((img) => {
    imageObserver.observe(img);
  });
};

/**
 * Virtual scroll helper for large lists
 * Improves performance by only rendering visible items
 */
export const createVirtualScrollHelper = (containerSelector, itemHeight, itemCount) => {
  const container = document.querySelector(containerSelector);
  if (!container) return null;

  let scrollTop = 0;
  const visibleItems = Math.ceil(container.clientHeight / itemHeight) + 2;
  
  const getVisibleRange = () => {
    const startIndex = Math.max(0, Math.floor(scrollTop / itemHeight) - 1);
    return {
      start: startIndex,
      end: Math.min(itemCount, startIndex + visibleItems)
    };
  };

  const onScroll = () => {
    scrollTop = container.scrollTop;
  };

  container.addEventListener('scroll', onScroll, { passive: true });

  return {
    getVisibleRange,
    destroy: () => container.removeEventListener('scroll', onScroll)
  };
};

/**
 * Debounce function for scroll/resize events
 */
export const debounce = (func, wait) => {
  let timeout;
  return function executedFunction(...args) {
    const later = () => {
      clearTimeout(timeout);
      func(...args);
    };
    clearTimeout(timeout);
    timeout = setTimeout(later, wait);
  };
};

/**
 * Throttle function for performance-critical events
 */
export const throttle = (func, limit) => {
  let inThrottle;
  return function (...args) {
    if (!inThrottle) {
      func.apply(this, args);
      inThrottle = true;
      setTimeout(() => (inThrottle = false), limit);
    }
  };
};

/**
 * Request idle callback polyfill
 */
export const requestIdleCallback = window.requestIdleCallback || ((cb) => {
  const start = Date.now();
  return setTimeout(() => {
    cb({
      didTimeout: false,
      timeRemaining: () => Math.max(0, 50 - (Date.now() - start))
    });
  }, 1);
});

/**
 * Safe area detection
 */
export const getSafeAreaInsets = () => {
  if (!window.CSS?.supports?.('padding', 'max(0px)')) {
    return { top: 0, right: 0, bottom: 0, left: 0 };
  }

  const root = document.documentElement;
  const styles = getComputedStyle(root);
  
  return {
    top: parseInt(styles.getPropertyValue('--safe-area-inset-top')) || 0,
    right: parseInt(styles.getPropertyValue('--safe-area-inset-right')) || 0,
    bottom: parseInt(styles.getPropertyValue('--safe-area-inset-bottom')) || 0,
    left: parseInt(styles.getPropertyValue('--safe-area-inset-left')) || 0
  };
};

/**
 * Detect if device is mobile
 */
export const isMobile = () => {
  return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent)
    || window.innerWidth <= 768;
};

/**
 * Progressive image loading helper
 */
export const createProgressiveImage = (lowSrc, highSrc) => {
  return new Promise((resolve) => {
    const img = new Image();
    img.onload = () => {
      resolve(highSrc);
    };
    img.onerror = () => {
      resolve(lowSrc);
    };
    img.src = highSrc;
  });
};

/**
 * Optimize image for mobile
 */
export const getOptimizedImageUrl = (url, width = 400, height = 400, quality = 80) => {
  if (!url) return '';
  if (url.startsWith('http') && !url.includes('?')) {
    // Add image optimization params if supported
    const separator = url.includes('?') ? '&' : '?';
    return `${url}${separator}w=${width}&h=${height}&q=${quality}&auto=format`;
  }
  return url;
};

/**
 * Handle keyboard visibility on mobile
 */
export const onKeyboardShow = (callback) => {
  const visualViewportAdjustment = new ResizeObserver(() => {
    callback({
      isVisible: window.visualViewport.height < window.innerHeight,
      height: window.innerHeight - window.visualViewport.height
    });
  });

  if (window.visualViewport) {
    visualViewportAdjustment.observe(document.body);
  }

  return () => visualViewportAdjustment.disconnect();
};

/**
 * Push notification helper
 */
export const requestNotificationPermission = async () => {
  if (!('Notification' in window)) {
    console.log('Notifications not supported');
    return false;
  }

  if (Notification.permission === 'granted') {
    return true;
  }

  if (Notification.permission !== 'denied') {
    const permission = await Notification.requestPermission();
    return permission === 'granted';
  }

  return false;
};

/**
 * Register service worker
 */
export const registerServiceWorker = async () => {
  if (!('serviceWorker' in navigator)) {
    console.log('Service Worker not supported');
    return null;
  }

  try {
    const existingRegistration = await navigator.serviceWorker.getRegistration('/service-worker.js');
    if (existingRegistration) {
      return existingRegistration;
    }

    const registration = await navigator.serviceWorker.register('/service-worker.js', {
      scope: '/',
      updateViaCache: 'none'
    });
    console.log('Service Worker registered:', registration);
    return registration;
  } catch (error) {
    console.error('Service Worker registration failed:', error);
    return null;
  }
};

/**
 * Check if app is in stand-alone mode (PWA)
 */
export const isStandAloneMode = () => {
  return window.navigator.standalone === true || window.matchMedia('(display-mode: standalone)').matches;
};

/**
 * Haptic feedback (vibration API)
 */
export const hapticFeedback = (pattern = [10]) => {
  if ('vibrate' in navigator) {
    try {
      navigator.vibrate(pattern);
    } catch (e) {
      console.log('Haptic feedback not available');
    }
  }
};

/**
 * Request camera access
 */
export const requestCameraAccess = async () => {
  try {
    const stream = await navigator.mediaDevices.getUserMedia({
      video: { facingMode: 'user' },
      audio: false
    });
    return stream;
  } catch (error) {
    console.error('Camera access denied:', error);
    return null;
  }
};

/**
 * Request microphone access
 */
export const requestMicrophoneAccess = async () => {
  try {
    const stream = await navigator.mediaDevices.getUserMedia({
      audio: { echoCancellation: true, noiseSuppression: true },
      video: false
    });
    return stream;
  } catch (error) {
    console.error('Microphone access denied:', error);
    return null;
  }
};

/**
 * Lock screen orientation
 */
export const lockOrientation = (orientation = 'portrait') => {
  if (typeof window !== 'undefined' && window.screen?.orientation?.lock) {
    window.screen.orientation.lock(orientation).catch(() => {
      console.log('Failed to lock orientation');
    });
  }
};

/**
 * Unlock screen orientation
 */
export const unlockOrientation = () => {
  if (typeof window !== 'undefined' && window.screen?.orientation?.unlock) {
    window.screen.orientation.unlock();
  }
};

/**
 * Scheduled task runner for background work
 */
export const scheduleTask = (task, delay = 5000) => {
  if ('scheduler' in window) {
    window.scheduler.postTask(task);
  } else if ('requestIdleCallback' in window) {
    requestIdleCallback(task);
  } else {
    setTimeout(task, delay);
  }
};
